/**
 * GAP-4 leftover — client-side T-15 starting-soon reminders.
 *
 * Piggybacks on the notification bell poll. Fires only while the user has the
 * app open (no cron). Dedupes via notifications.session_id + localStorage.
 */

import { useEffect, useRef } from 'react';
import { useParticipantSessions } from '../features/sessions-participant/hooks/useParticipantSessions';
import { isSessionCompleted, isSessionLive } from '../features/sessions-participant/utils/sessionHelpers';
import {
  hasRecentSessionNotification,
  notifySessionStartingSoon,
} from '../lib/notificationService';
import type { PrioritizationSession } from '../types/prioritization/session';

const THRESHOLD_MINUTES = 15;
const LOCAL_KEY_PREFIX = 'startingSoon:';

function sessionStartAt(session: PrioritizationSession): Date | null {
  if (session.scheduledFor) return new Date(session.scheduledFor);
  if (session.modeConfig?.opensAt) return new Date(session.modeConfig.opensAt);
  return null;
}

function localDedupeKey(sessionId: string): string {
  return `${LOCAL_KEY_PREFIX}${sessionId}:${THRESHOLD_MINUTES}`;
}

function alreadyFiredLocally(sessionId: string): boolean {
  try {
    return localStorage.getItem(localDedupeKey(sessionId)) === '1';
  } catch {
    return false;
  }
}

function markFiredLocally(sessionId: string): void {
  try {
    localStorage.setItem(localDedupeKey(sessionId), '1');
  } catch {
    // ignore
  }
}

export function useSessionStartingSoonNotifications(): void {
  const { userId, mySessions } = useParticipantSessions();
  const inFlight = useRef(false);

  useEffect(() => {
    if (!userId) return;

    const tick = async () => {
      if (inFlight.current) return;
      inFlight.current = true;
      try {
        const now = Date.now();
        for (const session of mySessions) {
          if (isSessionCompleted(session) || isSessionLive(session)) continue;
          const start = sessionStartAt(session);
          if (!start) continue;
          const msUntil = start.getTime() - now;
          if (msUntil <= 0 || msUntil > THRESHOLD_MINUTES * 60_000) continue;
          if (alreadyFiredLocally(session.id)) continue;

          const recent = await hasRecentSessionNotification({
            userId,
            event: 'session_starting_soon',
            sessionId: session.id,
          });
          if (recent) {
            markFiredLocally(session.id);
            continue;
          }

          const startsIn = Math.max(1, Math.ceil(msUntil / 60_000));
          await notifySessionStartingSoon(
            session.id,
            session.name || 'Session',
            startsIn,
            [userId],
          );
          markFiredLocally(session.id);
        }
      } finally {
        inFlight.current = false;
      }
    };

    void tick();
    const id = window.setInterval(() => {
      void tick();
    }, 60_000);
    return () => window.clearInterval(id);
  }, [userId, mySessions]);
}
